import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeartHandshake, Settings, Shield, Globe } from "lucide-react";
import { useLanguageStore } from "../store/languageStore";
import SettingsPanel from "./SettingsPanel";

const messages = {
  EN: {
    help: "Get Help Now",
    donate: "Donate",
    learn: "Learn More",
    settings: "Settings",
    trust: "Trusted by",
    users: "users",
    secure: "Secure & Private",
    multilingual: "Multilingual Support",
  },
  ES: {
    help: "Obtener Ayuda Ahora",
    donate: "Donar",
    learn: "Aprende Más",
    settings: "Configuración",
    trust: "Confiado por",
    users: "usuarios",
    secure: "Seguro y Privado",
    multilingual: "Soporte Multilingüe",
  },
  ZH: {
    help: "立即获取帮助",
    donate: "捐赠",
    learn: "了解更多",
    settings: "设置",
    trust: "由",
    users: "用户",
    secure: "安全且私密",
    multilingual: "多语言支持",
  },
  VI: {
    help: "Nhận Trợ Giúp Ngay",
    donate: "Quyên Góp",
    learn: "Tìm Hiểu Thêm",
    settings: "Cài đặt",
    trust: "Được tin tưởng bởi",
    users: "người dùng",
    secure: "An toàn & Riêng tư",
    multilingual: "Hỗ trợ đa ngôn ngữ",
  },
  FIL: {
    help: "Kumuha ng Tulong Ngayon",
    donate: "Mag-donate",
    learn: "Matuto Pa",
    settings: "Mga Setting",
    trust: "Pinapagana ng",
    users: "mga user",
    secure: "Pribado at Mabilis",
    multilingual: "Multilingual Support",
  },
  KO: {
    help: "지금 도움 받기",
    donate: "기부하기",
    learn: "더 알아보기",
    settings: "설정",
    trust: "개발자를 신뢰하는",
    users: "개발자",
    secure: "개인정보 보호 & 신속",
    multilingual: "다국어 지원",
  },
  AR: {
    help: "احصل على المساعدة الآن",
    donate: "تبرع",
    learn: "اعرف المزيد",
    settings: "الإعدادات",
    trust: "موجه بالذكاء الاصطناعي",
    users: "المستخدمين",
    secure: "خاص وسريع",
    multilingual: "دعم متعدد اللغات",
  },
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLanguage } = useLanguageStore();
  const [showSettings, setShowSettings] = useState(false);
  
  const currentMessages = messages[selectedLanguage as keyof typeof messages] || messages.EN;

  const handleHelpClick = () => {
    sessionStorage.setItem('scrollTarget', 'get-started-btn');
    navigate("/");
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  const handleDonateClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/");
    const donateSection = document.getElementById("donate");
    if (donateSection) donateSection.scrollIntoView({ behavior: "smooth" });
  };

  const handleLearnMoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    sessionStorage.setItem('scrollTarget', 'features');
    navigate("/");
  };


  
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur border-b border-gray-100 dark:border-gray-800 animate-fade-in">
      {/* Trust Ribbon */}
      <div className="max-w-4xl mx-auto px-4 py-2 text-sm">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-4">
            <button
              onClick={() => scrollToSection('testimonials')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <Shield className="w-4 h-4 text-teal-600" />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {currentMessages.trust} <span className="font-semibold">10,000+</span> {currentMessages.users}
              </span>
            </button>
            <button
              onClick={() => scrollToSection('get-started-btn')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity mt-2 sm:mt-0"
            >
              <HeartHandshake className="w-4 h-4 text-teal-600" />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {currentMessages.secure}
              </span>
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <Globe className="w-4 h-4 text-teal-600" />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {currentMessages.multilingual}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-4 pt-8">
        <div className="pt-2">
          <button
            onClick={handleLogoClick}
            className="flex items-center gap-2 text-lg sm:text-xl font-semibold hover:opacity-80 transition-opacity"
          >
            <HeartHandshake
              className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600"
              aria-hidden="true"
            />
            <span className="text-gray-900 dark:text-white">RightNowAid</span>
          </button>
        </div>
        <nav className="hidden sm:flex items-center justify-end gap-3 pt-4">
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label={currentMessages.settings}
            title={currentMessages.settings}
          >
            <Settings className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
          <button
            onClick={handleDonateClick}
            className="btn-secondary text-sm px-4 py-2 whitespace-nowrap"
            aria-label={currentMessages.donate}
          >
            {currentMessages.donate}
          </button>
          <button
            onClick={handleLearnMoreClick}
            className="btn-secondary text-sm px-4 py-2 whitespace-nowrap"
            aria-label={currentMessages.learn}
          >
            {currentMessages.learn}
          </button>
        </nav>
        {/* Mobile Navigation */}
        <div className="sm:hidden flex items-center gap-2 pt-4">
          <button
            onClick={handleDonateClick}
            className="btn-secondary text-xs px-2 py-1.5 whitespace-nowrap"
            aria-label={currentMessages.donate}
          >
            {currentMessages.donate}
          </button>
          <button
            onClick={handleLearnMoreClick}
            className="btn-secondary text-xs px-2 py-1.5 whitespace-nowrap"
            aria-label={currentMessages.learn}
          >
            {currentMessages.learn}
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label={currentMessages.settings}
            title={currentMessages.settings}
          >
            <Settings className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </div>

      <SettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </header>
  );
};

export default Header;